import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useRef } from "react";
import { Modal, useToast } from "@/components/common";
import { useAlert } from "@/hooks/useAlert";
import { contactsService } from "@/api/services/contacts.service";
// import { formatFullName } from "@/utils/string";
import styles from "./ClientPersonaTab.module.css";
const MAX_VISIBLE_NOTES = 6;
export const ClientPersonaTab = ({ contactId }) => {
    const { error: showError } = useToast();
    const { confirm } = useAlert();
    const [showAllNotes, setShowAllNotes] = useState(false);
    const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
    const [isEditNoteModalOpen, setIsEditNoteModalOpen] = useState(false);
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [newNote, setNewNote] = useState("");
    const [notes, setNotes] = useState([]);
    const [contact, setContact] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [expandedNotes, setExpandedNotes] = useState(new Set());
    const [isBioExpanded, setIsBioExpanded] = useState(false);
    const [needsTruncation, setNeedsTruncation] = useState(false);
    const bioRef = useRef(null);
    const visibleNotes = showAllNotes ? notes : notes.slice(0, MAX_VISIBLE_NOTES);
    // Helper function to parse text fields into arrays (split by newlines or bullet points)
    const parseListField = (text) => {
        if (!text)
            return [];
        return text
            .split("\n")
            .map((line) => line.trim())
            .filter((line) => line.length > 0)
            .map((line) => line.replace(/^[-•*]\s*/, "")); // Remove bullet points if present
    };
    const fetchContactData = useCallback(async () => {
        if (!contactId)
            return;
        try {
            setLoading(true);
            setError(null);
            const [contactResponse, notesResponse] = await Promise.all([
                contactsService.getContact(contactId),
                contactsService.getContactNotes(contactId),
            ]);
            setContact(contactResponse.data);
            setNotes(notesResponse.data);
        }
        catch (err) {
            console.error("Error fetching contact data:", err);
            setError("Failed to load contact data");
        }
        finally {
            setLoading(false);
        }
    }, [contactId]);
    const fetchNotes = useCallback(async () => {
        if (!contactId)
            return;
        try {
            setError(null);
            const response = await contactsService.getContactNotes(contactId);
            setNotes(response.data);
        }
        catch (err) {
            console.error("Error fetching notes:", err);
            setError("Failed to load notes");
        }
    }, [contactId]);
    useEffect(() => {
        fetchContactData();
    }, [fetchContactData]);
    // Measure if bio text needs truncation
    useEffect(() => {
        if (!bioRef.current || !contact?.companyBio) {
            setNeedsTruncation(false);
            return;
        }
        // Wait for DOM to render, then check if text is truncated
        const checkTruncation = () => {
            if (!bioRef.current)
                return;
            const element = bioRef.current;
            // Temporarily apply bioPreview class to measure truncated height
            // This checks if text would be truncated when collapsed
            const hadPreviewClass = element.classList.contains(styles.bioPreview);
            // Ensure preview class is applied for accurate measurement
            if (!hadPreviewClass) {
                element.classList.add(styles.bioPreview);
            }
            // Compare scrollHeight (full content) with clientHeight (visible height)
            // If scrollHeight > clientHeight, text is truncated
            const isTruncated = element.scrollHeight > element.clientHeight;
            setNeedsTruncation(isTruncated);
            // Restore original class state
            if (!hadPreviewClass) {
                element.classList.remove(styles.bioPreview);
            }
        };
        // Use requestAnimationFrame to ensure DOM has fully rendered
        requestAnimationFrame(() => {
            setTimeout(checkTruncation, 0);
        });
    }, [contact?.companyBio]);
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };
    const handleAddNote = () => {
        setIsAddNoteModalOpen(true);
    };
    const handleCloseModal = () => {
        setIsAddNoteModalOpen(false);
        setIsEditNoteModalOpen(false);
        setEditingNoteId(null);
        setNewNote("");
    };
    const handleSaveNote = async () => {
        if (!newNote.trim() || !contactId)
            return;
        try {
            setSaving(true);
            setError(null);
            const dto = { note: newNote.trim() };
            await contactsService.createContactNote(contactId, dto);
            await fetchNotes();
            handleCloseModal();
        }
        catch (err) {
            console.error("Error saving note:", err);
            setError("Failed to save note");
            showError("Failed to save note. Please try again.");
        }
        finally {
            setSaving(false);
        }
    };
    const handleEditNote = (note) => {
        setEditingNoteId(note.id);
        setNewNote(note.note);
        setIsEditNoteModalOpen(true);
    };
    const handleUpdateNote = async () => {
        if (!newNote.trim() || !editingNoteId || !contactId)
            return;
        try {
            setSaving(true);
            setError(null);
            const dto = { note: newNote.trim() };
            await contactsService.updateContactNote(contactId, editingNoteId, dto);
            await fetchNotes();
            handleCloseModal();
        }
        catch (err) {
            console.error("Error updating note:", err);
            setError("Failed to update note");
            showError("Failed to update note. Please try again.");
        }
        finally {
            setSaving(false);
        }
    };
    const handleDeleteNote = async (noteId) => {
        if (!contactId)
            return;
        const confirmed = await confirm("Are you sure you want to delete this note?");
        if (!confirmed)
            return;
        try {
            setError(null);
            await contactsService.deleteContactNote(contactId, noteId);
            await fetchNotes();
        }
        catch (err) {
            console.error("Error deleting note:", err);
            setError("Failed to delete note");
            showError("Failed to delete note. Please try again.");
        }
    };
    const toggleNoteExpand = (noteId) => {
        setExpandedNotes((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(noteId)) {
                newSet.delete(noteId);
            }
            else {
                newSet.add(noteId);
            }
            return newSet;
        });
    };
    if (loading) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.loadingState, children: _jsx("p", { children: "Loading persona data..." }) }) }));
    }
    if (error && !contact) {
        return (_jsx("div", { className: styles.container, children: _jsxs("div", { className: styles.errorState, children: [_jsx("p", { children: error }), _jsx("button", { onClick: fetchContactData, className: styles.retryButton, children: "Retry" })] }) }));
    }
    // Parse needs, pain points, and business style into arrays
    const needsList = parseListField(contact?.needs);
    const painPointsList = parseListField(contact?.painPoints);
    const businessStyleList = parseListField(contact?.businessStyle);
    return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.columnsGrid, children: [_jsx("div", { className: styles.column, children: _jsxs("div", { className: `${styles.card} ${styles.cardWithBorderBottom}`, children: [_jsx("h5", { className: styles.cardTitle, children: "Corporate Bio" }), contact?.companyBio ? (_jsxs(_Fragment, { children: [_jsx("p", { ref: bioRef, className: `${styles.text} ${!isBioExpanded ? styles.bioPreview : ""}`, children: contact.companyBio }), needsTruncation && (_jsx("button", { type: "button", className: styles.bioToggleButton, onClick: () => setIsBioExpanded((prev) => !prev), children: isBioExpanded ? "Show less" : "Read More >" }))] })) : (_jsx("p", { className: styles.emptyText, children: "No corporate bio available" }))] }) }), _jsx("div", { className: styles.column, children: _jsxs("div", { className: `${styles.card} `, children: [_jsx("h5", { className: styles.cardTitle, children: "Company Details" }), _jsxs("ul", { className: styles.list, children: [contact?.location && (_jsxs("li", { children: [_jsx("strong", { children: "Location" }), ": ", contact.location] })), contact?.companySize && (_jsxs("li", { children: [_jsx("strong", { children: "Company Size" }), ": ", contact.companySize] }))] }), !contact?.location && !contact?.companySize && (_jsx("p", { className: styles.emptyText, children: "No company details available" }))] }) }), _jsx("div", { className: styles.column, children: _jsxs("div", { className: `${styles.card}`, children: [_jsx("h5", { className: styles.cardTitle, children: "Sectors" }), contact?.sectors ? (_jsx("p", { className: styles.text, children: contact.sectors })) : (_jsx("p", { className: styles.emptyText, children: "No sectors information available" }))] }) }), _jsx("div", { className: styles.column, children: _jsxs("div", { className: styles.card, children: [_jsx("h5", { className: styles.cardTitle, children: "Needs" }), needsList.length > 0 ? (_jsx("ul", { className: styles.list, children: needsList.map((need, index) => (_jsx("li", { children: need }, index))) })) : (_jsx("p", { className: styles.emptyText, children: "No needs listed" }))] }) }), _jsx("div", { className: styles.column, children: _jsxs("div", { className: styles.card, children: [_jsx("h5", { className: styles.cardTitle, children: "Pain Points" }), painPointsList.length > 0 ? (_jsx("ul", { className: styles.list, children: painPointsList.map((point, index) => (_jsx("li", { children: point }, index))) })) : (_jsx("p", { className: styles.emptyText, children: "No pain points listed" }))] }) }), _jsx("div", { className: styles.column, children: _jsxs("div", { className: styles.card, children: [_jsx("h5", { className: styles.cardTitle, children: "Business Style" }), businessStyleList.length > 0 ? (_jsx("ul", { className: styles.list, children: businessStyleList.map((trait, index) => (_jsx("li", { children: trait }, index))) })) : (_jsx("p", { className: styles.emptyText, children: "No business style information available" }))] }) })] }), _jsx("div", { className: styles.notesSection, children: _jsxs("div", { className: `${styles.notesCard}  ${styles.cardWithBorder}`, children: [_jsxs("div", { className: styles.notesHeader, children: [_jsx("h5", { className: styles.cardTitle, children: "Client Notes" }), _jsx("button", { className: styles.addNoteButton, onClick: handleAddNote, children: "+ Add Note" })] }), loading ? (_jsx("div", { className: styles.loadingState, children: _jsx("p", { children: "Loading notes..." }) })) : error ? (_jsxs("div", { className: styles.errorState, children: [_jsx("p", { children: error }), _jsx("button", { onClick: fetchNotes, className: styles.retryButton, children: "Retry" })] })) : notes.length === 0 ? (_jsx("div", { className: styles.emptyState, children: _jsx("p", { children: "No notes yet. Add your first note above." }) })) : (_jsxs(_Fragment, { children: [_jsx("ul", { className: styles.notesList, children: visibleNotes.map((note) => {
                                        const isExpanded = expandedNotes.has(note.id);
                                        // const fullName = formatFullName(note.creator.firstName, note.creator.lastName);
                                        const fullName = note?.createdByName;
                                        const needsTruncation = note.note.split("\n").length > 3 || note.note.length > 150;
                                        return (_jsx("li", { className: styles.noteItem, children: _jsxs("div", { className: styles.noteCard, children: [_jsx("span", { className: `${styles.noteText} ${!isExpanded && needsTruncation
                                                            ? styles.noteTextTruncated
                                                            : ""}`, children: note.note }), _jsxs("div", { className: styles.noteFooter, children: [_jsxs("div", { className: styles.noteAuthorInfo, children: [_jsx("span", { className: styles.noteAuthor, children: fullName || "Unknown" }), _jsx("span", { className: styles.noteDate, children: formatDate(note.createdAt) })] }), _jsxs("div", { className: styles.noteActions, children: [_jsxs("button", { className: styles.editButton, onClick: () => handleEditNote(note), children: ["Edit", _jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" }), _jsx("path", { d: "M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" })] })] }), _jsx("span", { className: styles.separator, children: "|" }), _jsxs("button", { className: styles.deleteButton, onClick: () => handleDeleteNote(note.id), children: ["Delete", _jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("polyline", { points: "3 6 5 6 21 6" }), _jsx("path", { d: "M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" })] })] }), needsTruncation && (_jsxs(_Fragment, { children: [_jsx("span", { className: styles.separator, children: "|" }), _jsxs("button", { className: styles.expandButton, onClick: () => toggleNoteExpand(note.id), children: [isExpanded ? "Less" : "Expand", _jsx("svg", { width: "14", height: "14", viewBox: "0 -960 960 960", fill: "currentColor", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M120-120v-320h80v184l504-504H520v-80h320v320h-80v-184L256-200h184v80H120Z" }) })] })] }))] })] })] }) }, note.id));
                                    }) }), notes.length > MAX_VISIBLE_NOTES && (_jsx("button", { className: styles.showMoreButton, onClick: () => setShowAllNotes(!showAllNotes), children: showAllNotes ? "Hide notes" : "Show more notes" }))] }))] }) }), _jsx(Modal, { isOpen: isAddNoteModalOpen, onClose: handleCloseModal, title: "NEW", children: _jsxs("div", { className: styles.modalContent, children: [_jsx("textarea", { className: styles.noteTextarea, value: newNote, onChange: (e) => setNewNote(e.target.value), rows: 8, autoFocus: true, disabled: saving }), _jsxs("div", { className: styles.modalActions, children: [_jsx("button", { className: styles.cancelButton, onClick: handleCloseModal, disabled: saving, children: "Cancel" }), _jsx("button", { className: styles.saveNoteButton, onClick: handleSaveNote, disabled: !newNote.trim() || saving, children: saving ? "Saving..." : "Save Note" })] })] }) }), _jsx(Modal, { isOpen: isEditNoteModalOpen, onClose: handleCloseModal, title: "EDIT", children: _jsxs("div", { className: styles.modalContent, children: [_jsx("textarea", { className: styles.noteTextarea, value: newNote, onChange: (e) => setNewNote(e.target.value), rows: 8, autoFocus: true, disabled: saving }), _jsxs("div", { className: styles.modalActions, children: [_jsx("button", { className: styles.cancelButton, onClick: handleCloseModal, disabled: saving, children: "Cancel" }), _jsx("button", { className: styles.saveNoteButton, onClick: handleUpdateNote, disabled: !newNote.trim() || saving, children: saving ? "Updating..." : "Update Note" })] })] }) })] }));
};
